import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useOutletContext } from "react-router-dom"

import { useDirection } from "hooks/direction/useDirection"
import { Outlet } from "react-router-dom"
import { ErrorDialog } from "components/Dialog"
import { useTranslation } from "providers/i18n"
import { Head } from "../../../../../components/Head"
import { getFeatureDetail } from "utils/feature"
import { useCountdownTimer } from "hooks/useCountdownTimer"

export const DirectionLayout = () => {
  const {
    route,
    isError,
    origin,
    destination,
    isCalculatingRoute,
    exitPath,
    elevatorPreferredMode,
    toggleElevatorPreference,
  } = useDirection()

  const [redirectedToUserLocation, setRedirectedToUserLocation] =
    useState(false)

  const { t } = useTranslation({ defaultValue: null })
  const { name: destinationName = "" } = useMemo(
    () => (destination ? getFeatureDetail(destination) : {}),
    [destination]
  )

  const { name: originName = "" } = useMemo(
    () => (origin ? getFeatureDetail(origin) : {}),
    [origin]
  )
  const { scannedLocations, userLocation } = useOutletContext()
  const navigate = useNavigate()

  const handleCloseDialog = useCallback(() => {
    navigate(exitPath)
  }, [navigate, exitPath])

  const { countdownTimer } = useCountdownTimer({
    onComplete: handleCloseDialog,
    options: {
      enable: isError,
    },
  })

  useEffect(() => {
    if (!redirectedToUserLocation && !origin && destination && userLocation) {
      navigate(
        `/maps/dir/${userLocation.properties.userLocationParam}/${destination?.id}`,
        { replace: true }
      )
      setRedirectedToUserLocation(true)
    }
  }, [
    navigate,
    destination,
    userLocation,
    origin,
    redirectedToUserLocation,
    setRedirectedToUserLocation,
  ])

  return (
    <>
      <Head
        title={`${t("title.direction", {
          origin: t(originName),
          destination: t(destinationName),
        })}`}
      />
      <ErrorDialog
        open={isError}
        title={t("Navigation Error", { ns: "error" })}
        description={t(
          "The system was unable navigate because either the origin or the destination was not found which you have chosen.",
          { ns: "error" }
        )}
        onClose={handleCloseDialog}
        buttonText={`${t("Close")} (${countdownTimer})`}
      />
      {
        <Outlet
          context={
            route
              ? {
                  ...route,
                  route,
                  userLocation,
                  scannedLocations,
                  elevatorPreferredMode,
                  isCalculatingRoute,
                  toggleElevatorPreference,
                }
              : {
                  route,
                  origin,
                  destination,
                  userLocation,
                  scannedLocations,
                  elevatorPreferredMode,
                  isCalculatingRoute,
                  toggleElevatorPreference,
                }
          }
        />
      }
    </>
  )
}
